import { Header } from '../components/Header';
import { Funds } from '../components/Funds';
import { FormPFExportButton } from '../components/FormPFExportButton';
import { useForceUpdateReport } from '../../../services/report.service';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react';
import { usePeriodById } from '../../../services/period.service';
import { useGetGroupedWorkflows, useGetWorkflowById } from '../../../services/workflow.services';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePDF } from 'react-to-pdf';
import { FCACredentialsModal } from '../components/FCACredentialsModal';
import { UpdateReportModal } from '../components/UpdateReportModal';
import { useGetFundsByPeriodId } from '../../../services/funds.service';
import { FaRegSave } from 'react-icons/fa';
import { useReport } from './hooks/useReport';
import { useReportSections } from './hooks/useReportSections';
import { useUnsavedChangesModal } from './hooks/useUnsavedChangesModal';
import { useFCA } from '../hooks/useFCA';
import SubmitReportModal from '../components/SubmitReportModal';
import { DrillDown } from '../components/DrillDown';
import { ExportDrawer } from '../components/ExportDrawer';
import { useUserGroupsCurrentUserAdminState } from '../../../services/user.service';
import { useReportLinks } from './hooks/useReportLinks';
import { checkIsLockedAutomation } from '../../../utils/lockAutomation';
import reportStore from '../store';
import commonStore from '../../../store/common';
import { Box, Button, Flex, Loader, Notification } from '@mantine/core';
import { Sidebar } from './components/Sidebar';
import ReportCommentsSidebar from '../components/ReportComments/ReportCommentsSidebar';
import { Navbar } from './components/Navbar';
import { Sections } from './components/Sections';
import { UnsavedChangesModal } from './components/UnsavedChangesModal';
import { extractApiError } from '../../../utils';
import { ReportContextProvider } from './context/ReportContext';
import { reportTypesV1 } from './consts/v1/reportTypes';
import { REPORT_TYPE_IDS } from './consts/v1/reportTypes';
import { observer } from 'mobx-react-lite';
import { useWorkflowChrome } from '../../../components/Layout/WorkflowChromeContext';
import { useReportSignalRSync } from '../hooks/useReportSignalRSync';
import { useSignalRListener } from '../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../config/signalr';

const ReportContainer = forwardRef(
  (
    {
      id,
      isStaticTemplate = false,
      onOpen,
      hideTables = false,
      modalRef,
      isMultiFunds,
      handleChangeIsMultiFunds
    },
    ref
  ) => {
    const { workflowId, periodId } = useParams();

    // Component is an observer, so these reads are reactive without a subscription.
    const isDrillDownOpen = !!commonStore.drillDown;
    const commentsSidebarWidth = commonStore.commentsSidebarWidth;
    const drillDownWidth = commonStore.drillDownWidth;

    const [searchParams, setSearchParams] = useSearchParams();
    const [isOpenUpdateReport, setIsOpenUpdateReport] = useState(false);
    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [isErrorMsgShown, setIsErrorMsgShown] = useState(false);
    // Last dismissed report-load error — see the ESMA container for the pattern.
    const [dismissedError, setDismissedError] = useState(null);

    const jurisdictionId = searchParams.get('jurisdictionId');
    const isStaticTemplateModalOpen = searchParams.get('staticTemplateModalState') === 'open';

    const groupedWorkflows = useGetGroupedWorkflows({
      id: workflowId,
      queryParams: {
        enabled: !!workflowId && !!jurisdictionId
      }
    });

    const project = useGetWorkflowById({
      id: workflowId,
      queryParams: {
        enabled: !!workflowId && !jurisdictionId
      }
    });

    const { data: currentUser } = useUserGroupsCurrentUserAdminState();

    const { isLockedPeriod, setIsLockedPeriod } = reportStore;

    const period = usePeriodById({
      periodId,
      customKey: 'FORM_PF_PERIOD_BY_ID',
      queryOptions: {
        enabled: !!periodId && !jurisdictionId,
        onSuccess: res => {
          setIsErrorMsgShown(
            [
              res.modeling_status,
              res.transformation_progress_status,
              res.extraction_progress_status,
              res.jurisdiction_validation_progress_status
            ].includes('In progress') && res.report_progress_status === 'In progress'
          );

          return res;
        },
        cacheTime: 0,
        staleTime: 0
      }
    });

    // Refetch the period record when the backend fires a report-related
    // SignalR event so the "In progress" gate on Regenerate Report + the
    // header status readouts flip without a manual refresh.
    useReportSignalRSync({
      periodId,
      workflowId,
      onReportEvent: () => {
        period?.refetch?.();
        // Also pull the report body — the period-status refetch flips the header
        // gate, but rows served by GET /api/report/{periodId} stay stale otherwise.
        refetch?.();
      }
    });

    useEffect(() => {
      if (!period?.data || !currentUser) return;

      setIsLockedPeriod(checkIsLockedAutomation(period?.data, currentUser));
    }, [period?.data, currentUser]);

    const modelingStatus = period.data?.modeling_status;
    const forceUpdate = modelingStatus === 'Not started' || modelingStatus === 'In progress';

    const forceUpdateReport = useForceUpdateReport();

    const { targetRef } = usePDF({ filename: 'page.pdf' });
    const isAIF001 = false;
    const {
      onCreateFCA,
      onDeleteFCA,
      deletingFCA,
      updatingFCA,
      creatingFCA,
      hasErrorOnFCA,
      fcaCredentials,
      isOpenFCACredentials,
      setIsOpenFCACredentials
    } = useFCA({ workflowId, enabled: !!currentUser && currentUser.role !== 'Approver' });

    const {
      funds,
      currentDataKey,
      onSave,
      schema,
      refetch,
      isLoading,
      hasChanges,
      addRow,
      onDeleteRow,
      isLoadingData,
      onChangeFund,
      onChangeDataKey,
      onClearStaticData,
      isLoadingSchema,
      jurisdictions,
      isViewer,
      canRedo,
      canUndo,
      undo,
      redo,
      isLoadingUser,
      currentSection,
      currentReportType,
      onChangeReportType,
      pendingFundId,
      clearPendingFundId,
      applyFundChange,
      pendingReportTypeId,
      clearPendingReportTypeId,
      applyReportTypeChange,
      fundsWithStatus,
      revertCurrentFundChanges,
      allFunds,
      onChangeFundSelection,
      managerRequiredSections,
      isComparisonMode,
      toggleComparisonMode,
      reportLoadError,
      isReportLoadError
    } = useReport({
      forceUpdate,
      isStaticTemplate,
      period,
      periodId,
      setSearchParams,
      isMultiFunds
    });

    const inputFunds = useGetFundsByPeriodId({
      periodId: periodId,
      queryParams: {
        enabled: !jurisdictionId
      }
    });

    const links = useReportLinks({
      jurisdictionId,
      periodId,
      groupedWorkflows,
      project,
      period
    });

    const isMinimalFiling =
      period?.data?.filing_type === 'PF_TRANS' || period?.data?.filing_type === 'PF_FINAL';

    const pfTransSections = useMemo(() => {
      if (!isMinimalFiling) return null;
      return [
        {
          name: 'Section 1a',
          key: '1a',
          questions: [{ name: '2.1. Signature', tableName: 'REPRESENTATIVE_SIGNATURE' }]
        }
      ];
    }, [isMinimalFiling]);

    const isSignatureComplete = useMemo(() => {
      if (!isMinimalFiling) return true;
      const sig = funds?.[REPORT_TYPE_IDS.AIF001]?.fund_data?.REPRESENTATIVE_SIGNATURE?.[0];
      if (!sig) return false;
      const requiredFields = [
        'name',
        'signature',
        'signature_title',
        'email',
        'phone_number',
        'signature_date'
      ];
      return requiredFields.every(field => {
        const val = sig[field];
        return val !== null && val !== undefined && String(val).trim() !== '';
      });
    }, [isMinimalFiling, funds]);

    useImperativeHandle(ref, () => ({
      refetchAllData() {
        refetch();
      }
    }));

    // Same SignalR refetch pattern as ESMA/FCA + extraction page: refresh
    // report payload + period status on AutomationReportTaskUpdate so the
    // grid updates as soon as the backend signals.
    const handleReportStatusUpdate = useCallback(
      data => {
        if (data?.period_id && data.period_id !== periodId) return;
        period.refetch();
        refetch();
      },
      [periodId, period, refetch]
    );

    useSignalRListener(
      SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED,
      handleReportStatusUpdate
    );

    const {
      versionedSections,
      sectionTablesByVersion,
      expandedSections,
      expandedQuestions,
      onToggleSection,
      onToggleQuestion,
      virtuosoRef,
      onNavigate
    } = useReportSections({ currentReportType, managerRequiredSections, funds });

    const onNavigateToCommentTable = useCallback(
      tableName => {
        if (!tableName) return;
        const sections = versionedSections?.filteredSections || [];
        for (const section of sections) {
          const sectionKey = section.key || section.name;
          const questionsByName = new Map();
          (section.questions || []).forEach(q => {
            const name = q?.name || 'Question';
            if (!questionsByName.has(name)) {
              questionsByName.set(name, { tableNames: [] });
            }
            questionsByName.get(name).tableNames.push(q.tableName);
          });
          const mergedQuestions = Array.from(questionsByName.values());
          const questionIndex = mergedQuestions.findIndex(q =>
            q.tableNames.some(t => t === tableName || (t && tableName.includes(t)))
          );
          if (questionIndex >= 0) {
            onNavigate({ sectionKey, questionIndex, shouldExpand: true });
            return;
          }
        }
      },
      [versionedSections?.filteredSections, onNavigate]
    );

    const {
      isUnsavedModalOpen,
      handleUnsavedModalBack,
      handleUnsavedModalDiscard,
      handleUnsavedModalSave
    } = useUnsavedChangesModal({
      hasChanges,
      isLockedPeriod,
      pendingReportTypeId,
      clearPendingReportTypeId,
      applyReportTypeChange,
      pendingFundId,
      clearPendingFundId,
      applyFundChange,
      revertCurrentFundChanges,
      onSave
    });

    const onOpenUpdateReport = () => {
      setIsOpenUpdateReport(true);
    };

    const onCloseUpdateReport = () => {
      setIsOpenUpdateReport(false);
    };

    const onUpdateReport = (fund_id_list, fn, errorFn) => {
      forceUpdateReport.mutate(
        {
          periodId,
          body: {
            fund_id_list
          }
        },
        {
          onSuccess: () => {
            onCloseUpdateReport();
            period.refetch();
            refetch();
            fn();
            toast.success('Report has been updated');
          },
          onError: err => {
            errorFn?.();
            toast.error(extractApiError(err, 'Unable to update this report.'), {
              duration: 3500
            });
          }
        }
      );
    };

    const sectionRefs = useRef({});
    const [activeQuestionKey, setActiveQuestionKey] = useState(null);

    const [isExpandedQuickNavigation, setIsExpandedQuickNavigation] = useState(true);

    const toggleQuickNavigation = useCallback(() => {
      setIsExpandedQuickNavigation(prev => !prev);
    }, []);

    // BEST PRACTICE: Мемоизируем значение контекста на верхнем уровне компонента
    // (не внутри условного рендеринга, чтобы соблюдать правила хуков)
    const reportContextValue = useMemo(
      () => ({
        funds,
        schema,
        currentDataKey,
        onChangeFund,
        addRow,
        onDeleteRow,
        hasChanges,
        isViewer,
        canRedo,
        canUndo,
        undo,
        redo,
        onSave,
        refetch,
        onChangeDataKey,
        onClearStaticData,
        jurisdictions,
        currentSection,
        currentReportType,
        onChangeReportType,
        isStaticTemplateModalOpen,
        isComparisonMode
      }),
      [
        funds,
        schema,
        currentDataKey,
        onChangeFund,
        addRow,
        onDeleteRow,
        hasChanges,
        isViewer,
        canRedo,
        canUndo,
        undo,
        redo,
        onSave,
        refetch,
        onChangeDataKey,
        onClearStaticData,
        jurisdictions,
        currentSection,
        currentReportType,
        onChangeReportType,
        isStaticTemplateModalOpen,
        isComparisonMode
      ]
    );

    useEffect(() => {
      handleChangeIsMultiFunds(currentReportType === REPORT_TYPE_IDS.AIF002);
    }, [currentReportType]);

    const showComparisonReport =
      period?.data?.template_type !== 'sratch' && project?.data?.periods?.length > 1;

    const { setLabels, clearLabels } = useWorkflowChrome();
    useEffect(() => {
      if (isStaticTemplate) return;
      setLabels({
        projectLabel: project?.data?.name,
        periodLabel:
          period?.data?.year && period?.data?.period
            ? `${period.data.year}, ${period.data.period}`
            : undefined,
        showComments: true,
        workflowId,
        periodId,
        activeStageKey: 'report'
      });
      return () => clearLabels();
    }, [
      isStaticTemplate,
      project?.data?.name,
      period?.data?.year,
      period?.data?.period,
      workflowId,
      periodId,
      setLabels,
      clearLabels
    ]);

    // Stepper's Submission step deep-links here with `openSubmitDrawer=true`.
    // FormPF's submit is the SubmitReportModal directly (no FCA credentials
    // pre-step). Strip the sentinel so a refresh doesn't reopen the modal.
    useEffect(() => {
      if (searchParams.get('openSubmitDrawer') !== 'true') return;
      setIsSubmitModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('openSubmitDrawer');
      setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const containerId = id ?? 'formpf-report-container';
    if (isLoadingUser) return <div id={`${containerId}-loading-placeholder`} />;

    const shouldShowLoadError = isReportLoadError && reportLoadError !== dismissedError;
    const shouldShowUpdateNotification =
      !isStaticTemplate &&
      isErrorMsgShown &&
      !!currentUser &&
      currentUser.role !== 'Approver' &&
      !shouldShowLoadError;

    return (
      <>
        <UnsavedChangesModal
          id={`${containerId}-unsaved-changes-modal`}
          opened={isUnsavedModalOpen}
          onClose={handleUnsavedModalBack}
          onBack={handleUnsavedModalBack}
          onDiscard={handleUnsavedModalDiscard}
          onSaveChanges={handleUnsavedModalSave}
          reportType={currentReportType}
        />
        <Box
          id={containerId}
          h="100%"
          sx={() => ({
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          })}
        >
          {!isStaticTemplate && (
            <Header
              id={`${containerId}-header`}
              onOpen={onOpen}
              onClicFCACredentials={() => setIsOpenFCACredentials(true)}
              onOpenUpdateReport={onOpenUpdateReport}
              onOpenSubmit={() => setIsSubmitModalOpen(true)}
              isAIF001={currentReportType === REPORT_TYPE_IDS.AIF001}
              isFormPF={true}
              links={links}
              isViewer={isViewer}
              isMultiFunds={isMultiFunds}
              isReportInProgress={period?.data?.is_force_update_in_progress === true}
              assignedTo={project?.data?.assigned_to}
              extraActions={
                <FormPFExportButton
                  isDisabled={isLockedPeriod}
                  isMinimalFiling={isMinimalFiling}
                  isSignatureComplete={isSignatureComplete}
                />
              }
            />
          )}

          <Box
            id={`${containerId}-body`}
            bg="#f8f9fa"
            p="12px 16px 16px"
            sx={() => ({
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            })}
          >
            {!isStaticTemplate && (
              <Navbar
                id={`${containerId}-navbar`}
                handleChangeIsMultiFunds={handleChangeIsMultiFunds}
                isMultiFunds={isMultiFunds}
                onSaveReport={onSave}
                currentReportType={currentReportType}
                reportTypes={reportTypesV1}
                onChangeReportType={onChangeReportType}
                hasChanges={hasChanges}
                isViewer={isViewer}
                undo={undo}
                redo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                isLockedPeriod={isLockedPeriod}
                funds={allFunds}
                onChangeFundSelection={onChangeFundSelection}
                toggleComparisonMode={toggleComparisonMode}
                isComparisonMode={isComparisonMode}
                showComparisonReport={showComparisonReport}
                isMinimalFiling={isMinimalFiling}
              />
            )}

            <Flex gap="12px" mt="12px" style={{ flex: 1, minHeight: 0 }}>
              {!hideTables && (
                <Sidebar
                  id={`${containerId}-sidebar`}
                  sections={pfTransSections || versionedSections.filteredSections}
                  isStaticTemplate={isStaticTemplate}
                  isExpandedQuickNavigation={isExpandedQuickNavigation}
                  toggleQuickNavigation={toggleQuickNavigation}
                  expandedSections={expandedSections}
                  expandedQuestions={expandedQuestions}
                  activeQuestionKey={activeQuestionKey}
                  onToggleSection={onToggleSection}
                  onToggleQuestion={onToggleQuestion}
                  onNavigate={onNavigate}
                  funds={funds}
                  currentDataKey={currentDataKey}
                />
              )}

              <Box
                id={`${containerId}-content`}
                p="16px"
                sx={() => ({
                  flex: 1,
                  borderRadius: '4px',
                  border: '1px solid #f1f3f5',
                  backgroundColor: '#fff',
                  height: '100%',
                  overflow: 'auto',
                  marginRight: isDrillDownOpen ? drillDownWidth - commentsSidebarWidth : 0,
                  transition: 'margin-right 200ms ease'
                })}
              >
                {!isLoading &&
                  !isLoadingSchema &&
                  !isMinimalFiling &&
                  currentReportType === REPORT_TYPE_IDS.AIF002 &&
                  Object.keys(funds).length !== 0 && (
                    <Funds
                      currentDataKey={currentDataKey}
                      onChangeDataKey={onChangeDataKey}
                      funds={funds}
                      isStaticTemplate={isStaticTemplate}
                      onOpen={onOpen}
                      onClearStaticData={onClearStaticData}
                      isAIF001={isAIF001}
                      canRedo={canRedo}
                      canUndo={canUndo}
                      undo={undo}
                      redo={redo}
                      handleChangeIsMultiFunds={handleChangeIsMultiFunds}
                      isMultiFunds={isMultiFunds}
                    />
                  )}

                {(isLoading || isLoadingSchema) && (
                  <Flex
                    id={`${containerId}-loading-state`}
                    justify="center"
                    align="center"
                    h="100%"
                  >
                    <Loader color="blue" />
                  </Flex>
                )}

                {!isLoading && !isLoadingSchema && !hideTables && (
                  <ReportContextProvider value={reportContextValue}>
                    <Sections
                      isStaticTemplate={isStaticTemplate}
                      targetRef={targetRef}
                      sectionRefs={sectionRefs}
                      isMultiFunds={isMultiFunds}
                      modalRef={modalRef}
                      virtuosoRef={virtuosoRef}
                      sections={pfTransSections || versionedSections.filteredSections}
                      sectionTables={sectionTablesByVersion}
                      expandedSections={expandedSections}
                      expandedQuestions={expandedQuestions}
                      onChangeActiveSection={() => {}}
                      onChangeActiveQuestion={setActiveQuestionKey}
                      onToggleSection={onToggleSection}
                      onToggleQuestion={onToggleQuestion}
                    />
                  </ReportContextProvider>
                )}
              </Box>

              <ReportCommentsSidebar
                id={`${containerId}-comments-sidebar`}
                isMultiFunds={isMultiFunds}
                isStaticTemplate={isStaticTemplate}
                onNavigateToSection={onNavigateToCommentTable}
              />
            </Flex>
            {shouldShowUpdateNotification && (
              <Box
                id={`${containerId}-notification-wrapper`}
                pos="absolute"
                bottom="20px"
                w="400px"
                left="calc(50% - 200px)"
                sx={() => ({
                  zIndex: 9999
                })}
              >
                <Notification
                  id={`${containerId}-notification`}
                  onClose={() => setIsErrorMsgShown(prev => !prev)}
                  withCloseButton
                >
                  {`The source of report has been updated. Please update the report ${isAIF001 ? 'AIF002 ' : ''}(click
                "Regenerate Report") to sync with latest changes. Your previous report
                will be overwritten.`}
                </Notification>
              </Box>
            )}
            {shouldShowLoadError && (
              <Box
                id={`${containerId}-load-error-wrapper`}
                pos="absolute"
                bottom="20px"
                w="440px"
                left="calc(50% - 220px)"
                sx={() => ({ zIndex: 9999 })}
              >
                <Notification
                  id={`${containerId}-load-error`}
                  color="red"
                  title="Couldn't load the report"
                  withCloseButton
                  onClose={() => setDismissedError(reportLoadError)}
                >
                  {extractApiError(
                    reportLoadError,
                    'Failed to get all data. Please try again or contact support.'
                  )}
                </Notification>
              </Box>
            )}
          </Box>
        </Box>

        <UpdateReportModal
          id={`${containerId}-update-report-modal`}
          isLoading={forceUpdateReport.isLoading}
          onUpdateReport={onUpdateReport}
          inputFunds={inputFunds}
          isOpen={isOpenUpdateReport}
          onClose={onCloseUpdateReport}
          workflowId={workflowId}
          periodId={periodId}
          transformationStatus={period?.data?.transformation_progress_status}
          extractionStatus={period?.data?.extraction_progress_status}
          lookupStatus={period?.data?.lookup_progress_status}
        />

        <FCACredentialsModal
          id={`${containerId}-fca-credentials-modal`}
          key={isOpenFCACredentials}
          isOpen={isOpenFCACredentials}
          onClose={() => setIsOpenFCACredentials(false)}
          onSubmit={onCreateFCA}
          onDelete={onDeleteFCA}
          isLoadingDelete={deletingFCA}
          isLoading={creatingFCA || updatingFCA}
          credentials={hasErrorOnFCA ? null : fcaCredentials}
        />

        <SubmitReportModal
          id={`${containerId}-submit-report-modal`}
          isOpen={isSubmitModalOpen}
          onClose={() => setIsSubmitModalOpen(false)}
          periodId={periodId}
          funds={funds ? Object.values(funds) : []}
          refetchFunds={refetch}
        />

        {isStaticTemplate && !isLockedPeriod && (
          <Flex
            id={`${containerId}-save-footer`}
            justify="flex-end"
            p="16px"
            pos="absolute"
            left={0}
            right={0}
            bottom={0}
            bg="white"
            sx={() => ({
              zIndex: 999
            })}
          >
            <Button
              id={`${containerId}-save-button`}
              loading={isLoadingData}
              onClick={onSave}
              size="xs"
              leftSection={<FaRegSave />}
            >
              Save
            </Button>
          </Flex>
        )}

        <DrillDown id={`${containerId}-drilldown`} />
        <ExportDrawer
          id={`${containerId}-export-drawer`}
          jurisdictions={jurisdictions}
          periodId={periodId}
          fundIds={inputFunds?.data?.map(item => item.id)}
          hasWarning={fundsWithStatus.errors.length > 0}
          workflowId={workflowId}
        />
      </>
    );
  }
);

export default observer(ReportContainer);
